var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { batch, createProperty } from "@apendua/signal";
import { store, memo } from "@apendua/signal";
import { createRect, getFieldSetBBox, getNeighbors } from "@apendua.dev/geometry";
export * from "./types.js";
export function getKey({ x, y, z }) {
    return `${x},${y},${z}`;
}
export class Field {
    #state;
    #fields;
    #fieldSetup;
    constructor(props, fields) {
        const { uncovered, flag, ...fieldSetup } = props;
        this.#state = store({
            uncovered,
            flag,
        });
        this.#fields = fields;
        this.#fieldSetup = fieldSetup;
    }
    get key() {
        return this.#fieldSetup.key;
    }
    get mine() {
        return this.#fieldSetup.mine;
    }
    get uncovered() {
        return this.#state.uncovered;
    }
    get flag() {
        return this.#state.flag;
    }
    get x() {
        return this.#fieldSetup.x;
    }
    get y() {
        return this.#fieldSetup.y;
    }
    get z() {
        return this.#fieldSetup.z;
    }
    get neighbors() {
        return getNeighbors(this.#fieldSetup).flatMap((vector) => {
            const neighborKey = getKey(vector);
            const neighbor = this.#fields.getField(neighborKey);
            return neighbor ? [neighbor] : [];
        });
    }
    get nMines() {
        return this.neighbors.filter((neighbor) => neighbor.mine).length;
    }
    get statusText() {
        if (!this.uncovered) {
            if (this.flag) {
                return "?";
            }
            return "";
        }
        if (this.mine) {
            return "X";
        }
        if (this.nMines > 0) {
            return `${this.nMines}`;
        }
        return "";
    }
    uncover() {
        batch(() => {
            if (this.#state.uncovered || this.#state.flag || this.#fields.isGameOver) {
                return;
            }
            this.#state.uncovered = true;
            if (this.mine) {
                this.#fields.revealMines();
            }
            else if (this.nMines === 0) {
                this.neighbors.forEach((neighbor) => neighbor.uncover());
            }
        });
    }
    reveal() {
        this.#state.uncovered = true;
    }
    toggleFlag() {
        if (this.#fields.isGameOver || this.#state.uncovered) {
            return;
        }
        this.#state.flag = !this.#state.flag;
    }
}
__decorate([
    memo()
], Field.prototype, "neighbors", null);
export class Fields {
    #state;
    #fields = new Map();
    constructor(fields) {
        this.#state = store({ keys: Object.keys(fields) });
        for (const [key, field] of Object.entries(fields)) {
            const fieldData = {
                ...field,
                uncovered: false,
                flag: false,
            };
            this.#fields.set(key, new Field(fieldData, this));
        }
    }
    getField(key) {
        return this.#fields.get(key);
    }
    get fieldKeys() {
        return [...this.#state.keys];
    }
    get coveredFields() {
        return this.fieldKeys.flatMap((key) => {
            const field = this.getField(key);
            return field && !field.uncovered ? [field] : [];
        });
    }
    get totalMines() {
        return this.fieldKeys.filter((key) => {
            const field = this.getField(key);
            return field && field.mine;
        }).length;
    }
    get uncoveredFields() {
        return this.fieldKeys.flatMap((key) => {
            const field = this.getField(key);
            return field && field.uncovered ? [field] : [];
        });
    }
    get flaggedFields() {
        return this.fieldKeys.flatMap((key) => {
            const field = this.getField(key);
            return field && field.flag ? [field] : [];
        });
    }
    get numberOfCoveredMines() {
        return this.uncoveredFields.filter((field) => field.mine).length;
    }
    get minesLeft() {
        return this.totalMines - this.flaggedFields.length;
    }
    get isLost() {
        return this.uncoveredFields.some((field) => field.mine);
    }
    get isWon() {
        return (!this.isLost &&
            this.uncoveredFields.length > 0 &&
            this.coveredFields.length === this.totalMines);
    }
    get isGameOver() {
        return this.isWon || this.isLost;
    }
    revealMines() {
        batch(() => {
            for (const field of this.allFields) {
                if (field.mine) {
                    field.reveal();
                }
            }
        });
    }
    get allFields() {
        return this.fieldKeys.flatMap((key) => {
            const field = this.getField(key);
            return field ? [field] : [];
        });
    }
    get bounds() {
        return getFieldSetBBox(this.allFields);
    }
    static generate(options) {
        const { nRows = 15, nColumns = 15, minesRatio = 0.2 } = options;
        const fields = {};
        const vectors = createRect({ nRows, nColumns });
        for (const v of vectors) {
            const key = getKey(v);
            fields[key] = {
                ...v,
                key,
                mine: Math.random() < minesRatio,
            };
        }
        return new this(fields);
    }
}
__decorate([
    memo()
], Fields.prototype, "coveredFields", null);
__decorate([
    memo()
], Fields.prototype, "totalMines", null);
__decorate([
    memo()
], Fields.prototype, "uncoveredFields", null);
__decorate([
    memo()
], Fields.prototype, "flaggedFields", null);
__decorate([
    memo()
], Fields.prototype, "isLost", null);
__decorate([
    memo()
], Fields.prototype, "isWon", null);
export const FieldsContext = createProperty(() => new Fields({}));
//# sourceMappingURL=index.js.map